import { useContext, useEffect, useMemo, useRef, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";
import classNames from "classnames/bind";
import Button from "common/blocks/Button";
import Dropdown, { Option } from "common/blocks/Dropdown";
import Switch from "common/blocks/Switch";
import TextInput from "common/blocks/TextInput";
import SidebarContent from "common/blocks/VehicleAdCard/SidebarContent";
import useModalContext from "common/providers/modal/useModalContext";
import FinanceOptionsExplained from "features/vehicleDetails/modals/FinanceOptionsExplained";
import useFinanceDataContext from "features/vehicleDetails/providers/financeData/useFinanceDataContext";
import usePartExchangeQuestionsContext from "features/vehicleDetails/providers/partExchangeQuestions/usePartExchangeQuestionsContext";
import useKeysValidation from "hooks/useKeysValidation/useKeysValidation";
import { DIGITS_REGEX } from "lib/constants";
import { MonthlyPriceForProductResponse } from "models/monthlyPriceForProductModel";
import { Product, ProductVariant, getGrossPrice } from "models/productsByAvailabilityModel";
import { useForm } from "react-hook-form";
import { useQuery } from "react-query";
import { ResolvedHigherOfferSidePanel } from "resolvers/data/higherOfferSidePanelResolver";
import type { ResolvedRichText } from "resolvers/data/richTextResolver";
import type { ResolvedSidebarButton } from "resolvers/data/sidebarLinkResolver";
import type { ResolvedSidebar } from "resolvers/data/sidebarResolver";
import { ApiHostEnvelopeV2 } from "services/motorpoint/models/apiResponse";
import type { VehicleService } from "services/vehicle/vehicle";
import axiosOnSuccessEvent from "utils/axiosOnSuccessEvent";
import formatPrice from "utils/formatPrice";
import generateGtmFinanceSubmitEvent from "utils/generateGtmFinanceSubmitEvent";

import { representativeAPRLabel } from "../CalculatorStats";
import PartExchange from "../PartExchange";
import { FinanceCalculatorContext } from "../context";
import styles from "./CalculatorOptionsForm.module.scss";
import { HPMileageOptions, buildOptions } from "./helpers";
import useCalculateForDefault from "./hooks/useCalculateForDefault";
import { getFinanceCalculatorSchema } from "./schema";

const cx = classNames.bind(styles);

export type CalculatorOptionsFormProps = {
    stockNumber: VehicleService.Vehicle["StockBookNumber"];
    legacyVehicleId: VehicleService.Vehicle["LegacyVehicleId"];
    preferredDepositAmount: number;
    isPCPAvailable: boolean;
    isPCPCheapest: boolean;
    minDepositAmount: number;
    maxDepositAmount: number;
    isPxMoreThanMaxDeposit?: boolean;
    vehicleAmount: number;
    hasPX?: boolean;
    isCommercial: boolean;
    externalIds: VehicleService.ExternalId[];

    settings: {
        representativeApr: string;
        whatIsHPLinkButton: ResolvedSidebarButton;
        whatIsPCPLinkButton: ResolvedSidebarButton;
        addPartExchangeButtonLabel: string;
        addDepositButtonLabel: string;
        calculateButtonLabel: string;
        addDepositHelperMessage: string;
        modals: { [key: string]: ResolvedSidebar };
        pcpTermOptions: string[];
        hpTermOptions: string[];
        pcpMileageOptions: string[];
        calculationFailedMessage: ResolvedRichText;
        legalText: ResolvedRichText;
        legalTextHp: ResolvedRichText;
        higherOfferLabel: string;
        higherOfferButtonText: string;
        higherOfferSidePanel: ResolvedHigherOfferSidePanel;
        warrantySidebar?: ResolvedSidebar;
        commercialWarrantySidebar?: ResolvedSidebar;
        paintProtectionSidebar?: ResolvedSidebar;
        exteriorProtectionSidebar?: ResolvedSidebar;
    };
};

const formatPercentage = (value: string) => value.replaceAll("%", "<span>%<span>");

const CalculatorOptionsForm = ({
    stockNumber,
    preferredDepositAmount,
    isPCPAvailable,
    isPCPCheapest,
    minDepositAmount,
    maxDepositAmount,
    settings,
    hasPX,
    legacyVehicleId,
    isPxMoreThanMaxDeposit,
    vehicleAmount,
    isCommercial,
    externalIds,
}: CalculatorOptionsFormProps) => {
    const {
        getFinanceData,
        financeData,
        setIsHpShowing,
        depositFromFinanceWidget,
        setMilageSelection,
    } = useFinanceDataContext();
    const { partExQuestionsAnswersResponse } = usePartExchangeQuestionsContext();
    const { useSidebar } = useModalContext();
    const { registerSidebars, openRegisteredSidebar, openSidebar, isSidebarOpen } = useSidebar();
    const [hideAddDepositButton, setHideDepositButton] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [dataLoading, setDataLoading] = useState(false);
    const { handleKeyDownEvent, handlePasteEvent } = useKeysValidation();
    const formRef = useRef<HTMLFormElement | null>(null);
    const [paintProtectionProductVariants, setPaintProtectionProductVariants] = useState<Option[]>(
        []
    );
    const [warrantyProductVariants, setWarrantyProductVariants] = useState<Option[]>([]);
    const [exteriorProtectionProductVariants, setExteriorProtectionProductVariants] = useState<
        Option[]
    >([]);

    const {
        productVariants,
        updateProductVariants,
        selectedPaintProtectionExtraId,
        setSelectedPaintProtectionExtraId,
        selectedWarrantyExtraId,
        setSelectedWarrantyExtraId,
        selectedExteriorProtectionExtraId,
        setSelectedExteriorProtectionExtraId,
        setWarrantyPrice,
        setPaintProtectionPrice,
        setExteriorProtectionPrice,
    } = useContext(FinanceCalculatorContext);

    const vehicleTypeWarrantySidebar = isCommercial
        ? settings.commercialWarrantySidebar
        : settings.warrantySidebar;

    const isHPDefaultValue = !isPCPCheapest;

    const termOptions = useMemo(
        () => ({
            hp: buildOptions(settings.hpTermOptions, "months"),
            pcp: buildOptions(settings.pcpTermOptions, "months"),
        }),
        [settings.hpTermOptions, settings.pcpTermOptions]
    );

    const mileageOptions = useMemo(
        () => ({
            pcp: buildOptions(settings.pcpMileageOptions, "miles"),
            hp: HPMileageOptions,
        }),
        [settings.pcpMileageOptions]
    );

    const { formState, watch, setValue, handleSubmit, register } = useForm({
        defaultValues: {
            preferredDepositAmount: !!partExQuestionsAnswersResponse
                ? "0"
                : preferredDepositAmount.toString(),
            isHP: isHPDefaultValue,
            selectedTerm: isHPDefaultValue
                ? termOptions.hp[termOptions.hp.length - 1].value
                : termOptions.pcp[termOptions.pcp.length - 1].value,
            mileage: isHPDefaultValue ? mileageOptions.hp[0].value : mileageOptions.pcp[0].value,
            selectedWarranty: "0",
            selectedExteriorProtection: "0",
            selectedPaintProtection: "0",
        },
        resolver: zodResolver(
            getFinanceCalculatorSchema(
                minDepositAmount,
                maxDepositAmount,
                settings.addDepositHelperMessage
            )
        ),
    });

    const [selectedTerm, setSelectedTerm] = useState(
        isHPDefaultValue
            ? termOptions.hp[termOptions.hp.length - 1].value
            : termOptions.pcp[termOptions.pcp.length - 1].value
    );
    const capCodeValue = externalIds.find((x) => x.System.toUpperCase() == "CAP")?.Id ?? "";
    const { data } = useQuery(
        "financeCalculator",
        async () => {
            const response = await axios.post<Product[]>("/api/products/by-availability", {
                isBusinessCustomer: false,
                isCommercialVehicle: isCommercial,
                vehiclePrice: vehicleAmount,
                isUsageHireReward: false,
                capCode: capCodeValue,
                predicateType: "pdpFinanceCalculator",
            });
            axiosOnSuccessEvent(response);
            return response.data;
        },
        {
            refetchOnWindowFocus: false,
        }
    );

    const monthlyPriceForProduct = async (productPrice: number, term: number, isHP: boolean) => {
        const response = await axios.post<ApiHostEnvelopeV2<MonthlyPriceForProductResponse>>(
            "/api/finance/getMonthlyPriceForProduct",
            {
                vehicleId: legacyVehicleId,
                dealPriceExcludingProduct: vehicleAmount,
                productPrice: productPrice,
                term: isHP ? term : term - 1,
            }
        );
        axiosOnSuccessEvent(response);
        return response.data;
    };

    const populateVariants = async () => {
        if (!data) return;

        setDataLoading(true);

        const variantPromises = data.flatMap((d) => {
            return d.variants.map(async (v) => {
                updateProductVariants({
                    ...v,
                    isExtendedWarranty: d.isExtendedWarranty,
                    isPaintProtection: d.isPaintProtection,
                    isExteriorProtection: d.isExteriorProtection,
                });

                const monthlyPrice = await monthlyPriceForProduct(
                    getGrossPrice(v.price),
                    parseInt(`${selectedTerm}`),
                    isHP
                );
                if (!monthlyPrice.IsSuccess) {
                    return null;
                }

                if (d.isPaintProtection) {
                    return {
                        category: "paintProtection",
                        label: `Lifetime cover - ${formatPrice(
                            monthlyPrice?.Data?.MonthlyPrice || 0,
                            "Free",
                            true
                        )} per month`,
                        value: v.extraId.toString(),
                        data: getGrossPrice(v.price),
                    };
                } else if (d.isExtendedWarranty) {
                    const lengthOfCover = v.lengthOfCoverInMonths / 12;
                    return {
                        category: "warranty",
                        label: `${lengthOfCover} year${
                            lengthOfCover > 1 ? "s" : ""
                        } of cover - ${formatPrice(
                            monthlyPrice?.Data?.MonthlyPrice || 0,
                            "Free",
                            true
                        )} per month`,
                        value: v.extraId.toString(),
                        data: getGrossPrice(v.price),
                    };
                } else if (d.isExteriorProtection) {
                    const lengthOfCover = v.lengthOfCoverInMonths / 12;
                    return {
                        category: "exteriorProtection",
                        label: `${lengthOfCover} year plan - ${formatPrice(
                            monthlyPrice?.Data?.MonthlyPrice || 0,
                            "Free",
                            true
                        )} per month`,
                        value: v.extraId.toString(),
                        data: getGrossPrice(v.price),
                    };
                }

                return null;
            });
        });

        const resolvedVariants = (await Promise.all(variantPromises)).filter(Boolean) as Array<{
            category: string;
            label: string;
            value: string;
            data?: any;
        }>;

        const paintProtectionVariants = resolvedVariants
            .filter((v) => v.category === "paintProtection")
            .map(({ label, value, data }) => ({ label, value, data }));

        const warrantyVariants = resolvedVariants
            .filter((v) => v.category === "warranty")
            .map(({ label, value, data }) => ({ label, value, data }));

        const exteriorProtectionVariants = resolvedVariants
            .filter((v) => v.category === "exteriorProtection")
            .map(({ label, value, data }) => ({ label, value, data }));

        setPaintProtectionProductVariants(
            paintProtectionVariants[0]
                ? [{ label: "Please select", value: 0 }, ...paintProtectionVariants]
                : []
        );

        setWarrantyProductVariants(
            warrantyVariants[0] ? [{ label: "Please select", value: 0 }, ...warrantyVariants] : []
        );

        setExteriorProtectionProductVariants(
            exteriorProtectionVariants[0]
                ? [{ label: "Please select", value: 0 }, ...exteriorProtectionVariants]
                : []
        );

        setDataLoading(false);
    };

    useEffect(() => {
        populateVariants();
    }, [data, selectedTerm]);

    const transformedModals = useMemo(() => {
        if (!settings.modals) return {};

        return Object.keys(settings.modals).reduce(
            (res, key) => {
                res[key] = {
                    title: settings.modals[key].title,
                    content: {
                        component: FinanceOptionsExplained,
                        props: settings.modals[key].content,
                    },
                };
                return res;
            },
            {} as { [key: string]: any }
        );
    }, [settings.modals]);

    useEffect(() => {
        if (Object.keys(transformedModals).length) {
            registerSidebars(transformedModals);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [transformedModals]);

    useEffect(
        () => {
            setValue(
                "preferredDepositAmount",
                !!partExQuestionsAnswersResponse
                    ? "0"
                    : (depositFromFinanceWidget?.toString() ?? preferredDepositAmount.toString())
            );
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [partExQuestionsAnswersResponse, depositFromFinanceWidget]
    );

    useEffect(
        () => setHideDepositButton(!partExQuestionsAnswersResponse),
        [partExQuestionsAnswersResponse]
    );
    useCalculateForDefault(
        {
            ...formState.defaultValues,
            vehicleId: legacyVehicleId ?? stockNumber,
            depositFromFinanceWidget,
        },
        partExQuestionsAnswersResponse
    );

    const onSubmit = async (formData: any) => {
        setSubmitLoading(true);
        let warrantyPrice, paintPrice, exteriorPrice;
        let warrantyVariant: ProductVariant | undefined;
        let paintProtectionVariant: ProductVariant | undefined;
        let exteriorProtectionVariant: ProductVariant | undefined;
        const params = {
            ...formData,
            vehicleId: legacyVehicleId ?? stockNumber,
            selectedFinanceType: formData.isHP ? "HP" : "PCP",
            preferredDepositAmount: formData.preferredDepositAmount,
        };

        if (
            !!formData.selectedWarranty &&
            formData.selectedWarranty !== 0 &&
            formData.selectedWarranty !== "0"
        ) {
            warrantyPrice = warrantyProductVariants.filter(
                (v) => v.value === formData.selectedWarranty
            )[0];
            params.financeableProductPrices = params.financeableProductPrices ?? [];
            params.financeableProductPrices.push(warrantyPrice.data);

            warrantyVariant = productVariants.filter(
                (item) => item.isExtendedWarranty && item.extraId == formData.selectedWarranty
            )[0];
        }

        if (
            !!formData.selectedPaintProtection &&
            formData.selectedPaintProtection !== 0 &&
            formData.selectedPaintProtection !== "0"
        ) {
            paintPrice = paintProtectionProductVariants.filter(
                (v) => v.value === formData.selectedPaintProtection
            )[0];
            params.financeableProductPrices = params.financeableProductPrices ?? [];
            params.financeableProductPrices.push(paintPrice.data);

            paintProtectionVariant = productVariants.filter(
                (item) => item.isPaintProtection && item.extraId == formData.selectedPaintProtection
            )[0];
        }

        if (
            !!formData.selectedExteriorProtection &&
            formData.selectedExteriorProtection !== 0 &&
            formData.selectedExteriorProtection !== "0"
        ) {
            exteriorPrice = exteriorProtectionProductVariants.filter(
                (v) => v.value === formData.selectedExteriorProtection
            )[0];
            params.financeableProductPrices = params.financeableProductPrices ?? [];
            params.financeableProductPrices.push(exteriorPrice.data);

            exteriorProtectionVariant = productVariants.filter(
                (item) =>
                    item.isExteriorProtection && item.extraId == formData.selectedExteriorProtection
            )[0];
        }

        params.isHP = null;
        params.selectedWarranty = null;
        params.selectedPaintProtection = null;
        params.selectedExteriorProtection = null;

        const extrasString = [
            warrantyVariant?.description,
            paintProtectionVariant?.description,
            exteriorProtectionVariant?.description,
        ]
            .filter(Boolean)
            .join(", ");
        const submitEvent = generateGtmFinanceSubmitEvent(params, extrasString);
        await getFinanceData(params, submitEvent);

        setWarrantyPrice(
            !!warrantyPrice?.data
                ? new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(
                      warrantyPrice.data
                  )
                : ""
        );

        setPaintProtectionPrice(
            !!paintPrice?.data
                ? new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(
                      paintPrice.data
                  )
                : ""
        );

        setExteriorProtectionPrice(
            !!exteriorPrice?.data
                ? new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(
                      exteriorPrice.data
                  )
                : ""
        );

        setSubmitLoading(false);
    };

    const { onChange: onChangeIsHp, ...isHpRegister } = register("isHP");

    const customOnChangeIsHp = async (event: any) => {
        const isHPChecked = event.target.checked;

        await onChangeIsHp(event);

        setValue(
            "selectedTerm",
            isHPChecked
                ? termOptions.hp[termOptions.hp.length - 1].value
                : termOptions.pcp[termOptions.pcp.length - 1].value
        );
        setValue("mileage", isHPChecked ? mileageOptions.hp[0].value : mileageOptions.pcp[0].value);

        if (formRef.current) formRef.current.requestSubmit();
    };

    const representativeApr = (financeData || []).find(
        ({ Label }) => Label === representativeAPRLabel
    );

    const isHP = watch("isHP");
    const mileageSelection = watch("mileage");

    useEffect(() => {
        const milageSelectionAsNumber = parseInt(`${mileageSelection}`, 10);
        if (mileageSelection && !isNaN(milageSelectionAsNumber)) {
            setMilageSelection(milageSelectionAsNumber as number);
        } else {
            setMilageSelection(undefined);
        }
    }, [mileageSelection, setMilageSelection]);

    useEffect(() => {
        setIsHpShowing(isHP);
    }, [isHP, setIsHpShowing]);

    useEffect(() => {
        if (depositFromFinanceWidget) {
            setValue("preferredDepositAmount", depositFromFinanceWidget.toString());
            setHideDepositButton(true);
        }
    }, [depositFromFinanceWidget, setValue]);

    return (
        <form className={cx("options")} ref={formRef} onSubmit={handleSubmit(onSubmit)}>
            <div className={cx("typeApr")}>
                <div className={cx("stack", "type")}>
                    <span className={cx("label")}>Finance type</span>
                    {isPCPAvailable ? (
                        <div className={cx("switch")}>
                            <span className={cx("label", { checked: !isHP })}>PCP</span>
                            <Switch {...isHpRegister} onChange={customOnChangeIsHp} />
                            <span className={cx("label", { checked: isHP })}>HP</span>
                        </div>
                    ) : (
                        <span className={cx({ isOnlyHP: !isPCPAvailable })}>Hire Purchase</span>
                    )}
                </div>
                <div className={cx("stack", "apr")}>
                    <span className={cx("label")}>Representative APR</span>
                    <span
                        className={cx({ value: isPCPAvailable, isOnlyHP: !isPCPAvailable })}
                        dangerouslySetInnerHTML={{
                            __html: formatPercentage(
                                representativeApr?.FormattedValue ?? settings?.representativeApr
                            ),
                        }}
                    />
                </div>
            </div>
            <div className={cx("whatIs")}>
                {isPCPAvailable && (
                    <Button
                        ariaLabel={settings?.whatIsPCPLinkButton.label}
                        theme={settings?.whatIsPCPLinkButton.type}
                        onClick={() =>
                            openRegisteredSidebar(settings?.whatIsPCPLinkButton.params.sidebarId, {
                                openByDefault: settings?.whatIsPCPLinkButton.params.anchor,
                            })
                        }
                    >
                        {settings?.whatIsPCPLinkButton.label}
                    </Button>
                )}
                <Button
                    ariaLabel={settings?.whatIsHPLinkButton.label}
                    theme={settings?.whatIsHPLinkButton.type}
                    onClick={() =>
                        openRegisteredSidebar(settings?.whatIsHPLinkButton.params.sidebarId, {
                            openByDefault: settings?.whatIsHPLinkButton.params.anchor,
                        })
                    }
                >
                    {settings?.whatIsHPLinkButton.label}
                </Button>
            </div>
            <PartExchange buttonLabel={settings?.addPartExchangeButtonLabel} />

            {!isPxMoreThanMaxDeposit ? (
                hasPX && !hideAddDepositButton ? (
                    <Button
                        ariaLabel={settings?.addDepositButtonLabel}
                        onClick={() => setHideDepositButton(true)}
                        theme="secondary"
                        className={cx("button")}
                        fullWidth
                    >
                        {settings?.addDepositButtonLabel}
                    </Button>
                ) : null
            ) : null}

            <TextInput
                label="Your deposit amount"
                icon="Pound"
                helperText={settings.addDepositHelperMessage}
                hasError={!!formState.errors.preferredDepositAmount}
                errorText={formState?.errors?.preferredDepositAmount?.message as string}
                type={
                    isPxMoreThanMaxDeposit || (hasPX && !hideAddDepositButton) ? "hidden" : "text"
                }
                className={cx("addDepositInput")}
                alignRight
                {...register("preferredDepositAmount")}
                value={watch("preferredDepositAmount")}
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) =>
                    handleKeyDownEvent(event, DIGITS_REGEX)
                }
                onPaste={(event: React.ClipboardEvent<HTMLInputElement>) =>
                    handlePasteEvent(event, DIGITS_REGEX)
                }
                maxLength={Math.round(vehicleAmount).toString().length}
                // thousandSeparator=","
                pattern="\d*"
                inputMode="numeric"
            />

            <div className={cx("termMileage", { isHP })}>
                <Dropdown
                    label="Term"
                    ariaLabel="Term"
                    options={isHP ? termOptions.hp : termOptions.pcp}
                    {...register("selectedTerm")}
                    onChange={(e) => {
                        setSelectedTerm(+e.target.value);
                    }}
                    defaultValue={
                        isHP
                            ? termOptions.hp[termOptions.hp.length - 1].value
                            : termOptions.pcp[termOptions.pcp.length - 1].value
                    }
                />

                {!isHP && (
                    <Dropdown
                        label="Annual Mileage"
                        ariaLabel="Annual Mileage"
                        options={isHP ? mileageOptions.hp : mileageOptions.pcp}
                        {...register("mileage")}
                        defaultValue={
                            isHP
                                ? mileageOptions.hp[mileageOptions.hp.length - 1].value
                                : mileageOptions.pcp[mileageOptions.pcp.length - 1].value
                        }
                    />
                )}
            </div>

            {warrantyProductVariants[0] && (
                <Dropdown
                    label="Motorpoint Extended Warranty"
                    ariaLabel="Motorpoint Extended Warranty"
                    options={warrantyProductVariants}
                    {...register("selectedWarranty")}
                    defaultValue={0}
                    value={selectedWarrantyExtraId}
                    onChange={(e) => setSelectedWarrantyExtraId(+e.target.value)}
                    loading={dataLoading}
                    disabled={dataLoading || isSidebarOpen}
                    {...(!!vehicleTypeWarrantySidebar && {
                        dropdownIcon: {
                            uiIcon: {
                                icon: "InformationICircle",
                            },
                            onClick: () => {
                                const sidePanelContent: string | undefined =
                                    vehicleTypeWarrantySidebar?.content?.html;

                                if (sidePanelContent) {
                                    openSidebar({
                                        title: vehicleTypeWarrantySidebar?.title,
                                        content: {
                                            component: () => (
                                                <SidebarContent html={sidePanelContent} />
                                            ),
                                        },
                                    });
                                }
                            },
                        },
                    })}
                />
            )}

            {exteriorProtectionProductVariants[0] && (
                <Dropdown
                    label="Cosmetic and Alloy Wheel Maintenance Plan"
                    ariaLabel="Cosmetic and Alloy Wheel Maintenance Plan"
                    options={exteriorProtectionProductVariants}
                    {...register("selectedExteriorProtection")}
                    defaultValue={0}
                    value={selectedExteriorProtectionExtraId}
                    onChange={(e) => setSelectedExteriorProtectionExtraId(+e.target.value)}
                    loading={dataLoading}
                    disabled={dataLoading || isSidebarOpen}
                    {...(!!settings.exteriorProtectionSidebar && {
                        dropdownIcon: {
                            uiIcon: {
                                icon: "InformationICircle",
                            },
                            onClick: () => {
                                const sidePanelContent: string | undefined =
                                    settings.exteriorProtectionSidebar?.content?.html;

                                if (sidePanelContent) {
                                    openSidebar({
                                        title: settings.exteriorProtectionSidebar?.title,
                                        content: {
                                            component: () => (
                                                <SidebarContent html={sidePanelContent} />
                                            ),
                                        },
                                    });
                                }
                            },
                        },
                    })}
                />
            )}

            {paintProtectionProductVariants[0] && (
                <Dropdown
                    label="Williams Paint Protection"
                    ariaLabel="Williams Paint Protection"
                    options={paintProtectionProductVariants}
                    {...register("selectedPaintProtection")}
                    defaultValue={0}
                    value={selectedPaintProtectionExtraId}
                    onChange={(e) => setSelectedPaintProtectionExtraId(+e.target.value)}
                    loading={dataLoading}
                    disabled={dataLoading || isSidebarOpen}
                    {...(!!settings.paintProtectionSidebar && {
                        dropdownIcon: {
                            uiIcon: {
                                icon: "InformationICircle",
                            },
                            onClick: () => {
                                const sidePanelContent: string | undefined =
                                    settings.paintProtectionSidebar?.content?.html;

                                if (sidePanelContent) {
                                    openSidebar({
                                        title: settings.paintProtectionSidebar?.title,
                                        content: {
                                            component: () => (
                                                <SidebarContent html={sidePanelContent} />
                                            ),
                                        },
                                    });
                                }
                            },
                        },
                    })}
                />
            )}

            <Button
                theme="primary"
                className={cx("button")}
                type="submit"
                ariaLabel={settings?.calculateButtonLabel}
                fullWidth
                loading={submitLoading}
            >
                {settings?.calculateButtonLabel}
            </Button>
        </form>
    );
};

export default CalculatorOptionsForm;
